import { Typography } from "@mui/material";
import { ExternalLink } from "ExternalLink";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useReward } from "api/rewards";
import { useSegments } from "api/segments";
import { SelectOption } from "components/EffectSearch/EffectSearch";
import { DragDropInput } from "components/FormControls/DragDropInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { Item } from "components/FormControls/Select/Categories";
import { SelectInput } from "components/FormControls/Select/Select";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Field, Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ImportTypes, ImportTypesUnion } from "types/import";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { useStyles } from "./ImportCsvModal.styles";

const Dropdown = ({ importType }: { importType?: ImportTypesUnion }) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { useSegmentsList } = useSegments();
  const { useRewardList } = useReward();
  const [searchedElement, setSearchedElement] = useState("");

  const { data: segmentsList } = useSegmentsList({
    filters: searchedElement
      ? [
          {
            condition: "like",
            query: "name",
            value: searchedElement,
          },
          {
            query: "active",
            condition: "eq",
            value: "true",
          },
        ]
      : [
          {
            query: "active",
            condition: "eq",
            value: "true",
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const { data: rewardsList } = useRewardList({
    filters: searchedElement
      ? [
          {
            condition: "like",
            query: "name",
            value: searchedElement,
          },
          {
            query: "active",
            condition: "eq",
            value: "true",
          },
        ]
      : [
          {
            query: "active",
            condition: "eq",
            value: "true",
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  // Temporary filtering until static segments can be filtered on BE
  const staticSegments = segmentsList?.items.filter(
    (segment) => segment?.parts[0]?.criteria[0]?.type === "customer_csv_list",
  );

  const couponRewardTypes = [
    "static_coupon",
    "dynamic_coupon",
    "conversion_coupon",
  ];
  const couponRewards = rewardsList?.items.filter(
    (item) =>
      couponRewardTypes.includes(item.reward) && isEmpty(item.couponGenerator),
  );

  const getElementsLabels = (): SelectOption[] => {
    switch (importType) {
      case ImportTypes.SEGMENT_MEMBERS:
        return (
          staticSegments?.map(({ name, segmentId }) => ({
            label: name,
            id: segmentId,
          })) || []
        );
      case ImportTypes.REWARD_COUPON:
        return (
          couponRewards?.map(({ name, rewardId }) => ({
            label: name,
            id: rewardId,
          })) || []
        );
      default:
        return [];
    }
  };

  const getDropdownLabel = () => {
    switch (importType) {
      case ImportTypes.SEGMENT_MEMBERS:
        return "segment";
      case ImportTypes.REWARD_COUPON:
        return "reward";
      default:
        return "";
    }
  };

  const getSelectName = () => {
    switch (importType) {
      case ImportTypes.SEGMENT_MEMBERS:
        return "additionalData.segmentId";
      case ImportTypes.REWARD_COUPON:
        return "additionalData.rewardId";
      default:
        return "";
    }
  };

  return (
    <Field>
      {({ form }: { form: any }) => (
        <SelectInput
          className={classes.dropdown}
          displaySearch
          items={getElementsLabels()}
          label={
            <RequiredFieldLabel text={message({ id: getDropdownLabel() })} />
          }
          name={getSelectName()}
          asyncSearch={(searchedQuery: string) => {
            setSearchedElement(searchedQuery);
          }}
          onChange={(_, value) => form.setFieldValue(getSelectName(), value)}
        />
      )}
    </Field>
  );
};

interface Props {
  importType?: ImportTypesUnion;
  dropdown?: boolean;
  identificationFactor?: boolean;
  isModalOpen: boolean;
  headerMessage: string;
  closeModal: () => void;
  openImportStartedModal?: () => void;
  importData: (
    id: string,
    file?: string | Blob,
    identificationFactor?: string,
  ) => Promise<{
    data: any;
  }>;
  importDataId?: string;
}

export const ImportCsvModal = ({
  importType,
  dropdown = false,
  identificationFactor = false,
  isModalOpen,
  headerMessage,
  closeModal,
  importData,
  openImportStartedModal,
  importDataId,
}: Props) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { id } = useParams<{ id: string }>();

  const identificationFactorsItems: Item[] = [
    { id: "email", label: "email" },
    { id: "loyaltyCardNumber", label: "loyaltyCardNumber" },
    { id: "phone", label: "phone" },
    { id: "uuid", label: "memberID" },
  ].map((item) => ({
    id: item.id,
    label: message({
      id: item.label,
    }),
  }));

  return (
    <OLModal
      header={message({ id: headerMessage })}
      onClose={() => closeModal()}
      open={isModalOpen}
      data-test-id="import-csv-modal"
    >
      <Formik
        initialStatus={{}}
        initialValues={{
          import: undefined,
          additionalData: {
            identificationMethod: undefined,
            segmentId: undefined,
            rewardId: undefined,
          },
        }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});

          const getElementId = () => {
            if (
              dropdown &&
              typeof values?.additionalData?.segmentId === "string"
            ) {
              return values.additionalData.segmentId;
            }

            if (
              dropdown &&
              typeof values?.additionalData?.rewardId === "string"
            ) {
              return values.additionalData.rewardId;
            }

            if (typeof importDataId === "string") {
              return importDataId;
            }

            return id as string;
          };

          const elementId = getElementId();

          const identificationMethod = identificationFactor
            ? values.additionalData.identificationMethod
            : undefined;

          try {
            const response = await importData?.(
              elementId,
              values.import?.[0],
              identificationMethod,
            );
            if (response) {
              closeModal();
              if (openImportStartedModal) {
                openImportStartedModal();
              } else {
                if (location.pathname === "/imports-exports") {
                  notify(message({ id: "importHasStarted" }));
                } else {
                  notify(message({ id: "importHasStartedViewProgress" }), {
                    button: {
                      text: message({ id: "view" }),
                      onClick: ({ closeNotification }) => {
                        closeNotification();
                        return navigate(links.importsExports, {
                          state: { activeTab: "imports" },
                        });
                      },
                    },
                  });
                }
              }
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, status, values }) => {
          const formError = status && (status[""] || status["file"]);
          return (
            <Form onSubmit={handleSubmit}>
              {dropdown && importType && <Dropdown importType={importType} />}
              {identificationFactor && (
                <SelectInput
                  data-test-id="identification-factor"
                  name="additionalData.identificationMethod"
                  className={classes.dropdown}
                  items={identificationFactorsItems}
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "identificationFactor" })}
                    />
                  }
                  onChange={(value) =>
                    setFieldValue(
                      "additionalData.identificationMethod",
                      value.id,
                    )
                  }
                />
              )}
              {formError && <span className={classes.error}>{formError}</span>}
              <DragDropInput
                data-test-id="import-csv-modal"
                className={classes.dragDropWrapper}
                description={
                  <>
                    <Typography className={classes.dragDropText}>
                      {message({ id: "uploadCsvInfo" })}
                    </Typography>
                    <Typography className={classes.csvSizeInfo}>
                      {message(
                        { id: "csvSizeInfo" },
                        {
                          size: 100,
                        },
                      )}
                    </Typography>
                  </>
                }
                header={message({
                  id: "uploadFile",
                })}
                inputClassName={classes.fullWidth}
                singleFile
                onChange={(acceptedFiles, rejectedFiles) => {
                  setFieldValue(
                    "import",
                    acceptedFiles.length > 0
                      ? [acceptedFiles[0]]
                      : [rejectedFiles[0].file],
                  );
                }}
                onDelete={() => setFieldValue("import", undefined)}
                value={values.import}
                error={!!formError}
              />
              <Typography className={classes.linksText}>
                {message(
                  { id: "importCsvModalLinksText" },
                  {
                    importGuide: (
                      <ExternalLink path="https://help.openloyalty.io/imports-exports/imports">
                        {message({
                          id: "importGuide",
                        }).toLowerCase()}
                      </ExternalLink>
                    ),
                    sampleCsvFile: (
                      <ExternalLink path="https://help.openloyalty.io/imports-exports/imports/sample-import-files">
                        {message({
                          id: "sampleCsvFileLink",
                        })}
                      </ExternalLink>
                    ),
                  },
                )}
              </Typography>
              <OLButton
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting || !values.import}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="import-csv-modal-import-button"
              >
                {message({ id: "import" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
